// useSocket - React hook for Socket.io integration
// Implements US-4.1: WebSocket Infrastructure and Presence System
// Implements US-4.2: Real-time Synchronization of Workspace Changes
// Provides socket connection and real-time event handling for workspace components

import { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { connectSocket, disconnectSocket, joinWorkspace } from '../services/socketService';
import {
  ParticipantPresence,
  ParticipantJoinedEvent,
  ParticipantLeftEvent,
  ItemCreatedEvent,
  ItemUpdatedEvent,
  ItemDeletedEvent,
  ScoresUpdatedEvent,
  WorkspaceUpdatedEvent,
  WorkspaceClosedEvent,
} from '../types/socket';

/**
 * Event handler callbacks
 */
export interface SocketEventHandlers {
  // PRESENCE event handlers
  onPresenceUpdated?: (presence: ParticipantPresence[]) => void;
  onParticipantJoined?: (event: ParticipantJoinedEvent) => void;
  onParticipantLeft?: (event: ParticipantLeftEvent) => void;

  // ITEM event handlers
  onItemCreated?: (event: ItemCreatedEvent) => void;
  onItemUpdated?: (event: ItemUpdatedEvent) => void;
  onItemDeleted?: (event: ItemDeletedEvent) => void;

  // SCORE event handlers
  onScoresUpdated?: (event: ScoresUpdatedEvent) => void;

  // WORKSPACE event handlers
  onWorkspaceUpdated?: (event: WorkspaceUpdatedEvent) => void;
  onWorkspaceClosed?: (event: WorkspaceClosedEvent) => void;
}

/**
 * Hook parameters
 */
export interface UseSocketParams {
  handlers?: SocketEventHandlers;
}

/**
 * Hook return value
 */
export interface UseSocketReturn {
  // CONNECTION state
  isConnected: boolean;

  // SOCKET instance (for passing to child components)
  socket: ReturnType<typeof connectSocket> | null;

  // JOIN workspace function
  join: (workspaceId: string) => void;
}

/**
 * REACT HOOK for Socket.io integration
 * Manages WebSocket connection, workspace join, and presence tracking
 *
 * Usage:
 * ```tsx
 * const { presence, isConnected, join } = useSocket({
 *   handlers: {
 *     onItemCreated: (event) => { ... },
 *     onItemUpdated: (event) => { ... },
 *   }
 * });
 *
 * useEffect(() => {
 *   join(workspaceId);
 * }, [workspaceId]);
 * ```
 *
 * @param params - Hook parameters with event handlers
 * @returns Socket connection state and presence data
 */
export function useSocket(params?: UseSocketParams): UseSocketReturn {
  const { handlers } = params || {};

  const [isConnected, setIsConnected] = useState(false);

  // STORE socket reference to avoid calling connectSocket() multiple times
  const socketRef = useRef<ReturnType<typeof connectSocket> | null>(null);

  // TRACK pending workspace joins to prevent duplicate 'connect' listeners
  // This prevents duplicate joins when React Strict Mode calls join() twice before connection
  const pendingJoinRef = useRef<string | null>(null);

  // TRACK joined workspace to prevent duplicate joins when already connected
  // This prevents duplicate joins when React Strict Mode calls join() twice after connection
  const joinedWorkspaceRef = useRef<string | null>(null);

  // GET auth state to wait for authentication before connecting
  const { loading } = useAuth();

  // INITIALIZE socket connection on mount (run ONCE)
  // WAIT for authentication to complete before connecting to prevent race condition
  useEffect(() => {
    // SKIP connection if auth is still loading
    if (loading) {
      return;
    }

    const socket = connectSocket();
    socketRef.current = socket; // Store socket reference

    // DEDUPLICATION CHECK: Prevent duplicate listener registration in React Strict Mode
    // React Strict Mode: mount → unmount → remount causes this effect to run twice
    // Socket is a singleton, so we mark it to avoid registering listeners twice
    const socketAny = socket as any;

    // HANDLERS REF: Store handlers ref on socket object (not component)
    // This ensures all mounts use the same ref instance, preventing stale closures
    if (!socketAny._arbiterHandlersRef) {
      socketAny._arbiterHandlersRef = { current: handlers };
    } else {
      socketAny._arbiterHandlersRef.current = handlers;
    }
    const handlersRef = socketAny._arbiterHandlersRef;

    if (socketAny._arbiterListenersRegistered) {
      // SET initial connection state even when skipping registration
      setIsConnected(socket.connected);
      return; // Skip listener registration, but don't return cleanup
    }

    // MARK socket as having listeners registered
    // This flag persists across React Strict Mode mount/unmount cycles
    socketAny._arbiterListenersRegistered = true;

    // HANDLE connection status changes
    const handleConnect = () => {
      console.log('✓ useSocket: Connected', { socketId: socket.id });
      setIsConnected(true);
    };

    const handleDisconnect = () => {
      console.log('✗ useSocket: Disconnected');
      setIsConnected(false);
    };

    // CREATE stable wrapper functions that call handlers from ref
    // This allows handlers to update without re-registering event listeners
    // PRESENCE handlers
    const handlePresenceUpdate = (newPresence: ParticipantPresence[]) => {
      handlersRef.current?.onPresenceUpdated?.(newPresence);
    };

    const handleParticipantJoined = (data: ParticipantJoinedEvent) => {
      handlersRef.current?.onParticipantJoined?.(data);
    };

    const handleParticipantLeft = (data: ParticipantLeftEvent) => {
      handlersRef.current?.onParticipantLeft?.(data);
    };

    // CREATE stable wrapper functions that call handlers from ref
    // This allows handlers to update without re-registering event listeners
    const handleItemCreated = (event: ItemCreatedEvent) => {
      handlersRef.current?.onItemCreated?.(event);
    };
    const handleItemUpdated = (event: ItemUpdatedEvent) => {
      handlersRef.current?.onItemUpdated?.(event);
    };
    const handleItemDeleted = (event: ItemDeletedEvent) => {
      handlersRef.current?.onItemDeleted?.(event);
    };
    const handleScoresUpdated = (event: ScoresUpdatedEvent) => {
      handlersRef.current?.onScoresUpdated?.(event);
    };
    const handleWorkspaceUpdated = (event: WorkspaceUpdatedEvent) => {
      handlersRef.current?.onWorkspaceUpdated?.(event);
    };
    const handleWorkspaceClosed = (event: WorkspaceClosedEvent) => {
      handlersRef.current?.onWorkspaceClosed?.(event);
    };

    // REGISTER event listeners (these never change, so no race conditions)
    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('presence:updated', handlePresenceUpdate);
    socket.on('participant:joined', handleParticipantJoined);
    socket.on('participant:left', handleParticipantLeft);

    // REGISTER workspace change event listeners
    socket.on('item:created', handleItemCreated);
    socket.on('item:updated', handleItemUpdated);
    socket.on('item:deleted', handleItemDeleted);
    socket.on('scores:updated', handleScoresUpdated);
    socket.on('workspace:updated', handleWorkspaceUpdated);
    socket.on('workspace:closed', handleWorkspaceClosed);

    // SET initial connection state
    setIsConnected(socket.connected);

    // CLEANUP on unmount
    return () => {

      // NOTE: We do NOT remove listeners or the registration flag here!
      // Reason: Socket is a singleton shared across components
      // React Strict Mode will unmount/remount, but socket stays alive
      // If we cleanup here, remount will register duplicate listeners

      // Only cleanup happens on true app unmount (logout, tab close)
      // which is handled by disconnectSocket() in socketService

      // NOTE: Don't disconnect socket on component unmount
      // Socket connection is shared across components
      // Only disconnect when user logs out or closes tab
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]); // Run when loading changes (wait for auth to complete)

  // JOIN workspace callback
  // Waits for socket to be connected before joining
  const join = useCallback((workspaceId: string) => {
    const socket = socketRef.current;

    if (!socket) {
      return;
    }

    // DEDUPLICATION: Check if already joined this workspace
    // Prevents duplicate joins when React Strict Mode calls join() twice
    if (joinedWorkspaceRef.current === workspaceId) {
      return;
    }

    // CASE 1: Socket already connected → join immediately
    if (socket.connected) {
      try {
        joinWorkspace(workspaceId);
        joinedWorkspaceRef.current = workspaceId; // Mark as joined
        pendingJoinRef.current = null; // Clear any pending join
      } catch (error) {
        console.error('Failed to join workspace:', error);
      }
      return;
    }

    // CASE 2: Socket not yet connected → wait for 'connect' event
    // DEDUPLICATION: Check if already waiting to join this workspace
    if (pendingJoinRef.current === workspaceId) {
      return;
    }

    pendingJoinRef.current = workspaceId; // Mark as pending

    const handleConnectAndJoin = () => {
      try {
        joinWorkspace(workspaceId);
        joinedWorkspaceRef.current = workspaceId; // Mark as joined
        pendingJoinRef.current = null; // Clear pending join
      } catch (error) {
        console.error('Failed to join workspace after connection:', error);
      }
      // Remove listener after joining
      socket.off('connect', handleConnectAndJoin);
    };

    // Listen for connection, then join
    socket.once('connect', handleConnectAndJoin);

    // SAFETY: Timeout in case connection takes too long
    const timeout = setTimeout(() => {
      socket.off('connect', handleConnectAndJoin);
      pendingJoinRef.current = null; // Clear pending join on timeout
    }, 10000); // 10 second timeout

    // Clear timeout if we connect successfully
    socket.once('connect', () => clearTimeout(timeout));
  }, []);

  return {
    isConnected,
    socket: socketRef.current,
    join,
  };
}

/**
 * DISCONNECT socket when user logs out
 * Call this from logout handlers
 */
export function disconnectOnLogout(): void {
  disconnectSocket();
}
